/**
 * lib/employerMembersData.js — Phase 0.7 Slice S7/S8 read surface.
 *
 * All reads flow through the Supabase client and are governed
 * authoritatively by the live RLS posture established at S6:
 *   - Admin (V.III) sees all account rows
 *   - Operator (V.IV) sees cross-account rows
 *   - Reviewer (V.I) sees only own actor/target rows on audit
 *     log + own accepted invitation + own suspension record
 *
 * The frontend NEVER duplicates this filter logic — RLS is the
 * single source of visibility truth. Empty results are an
 * expected normal state (e.g. Reviewer with no suspensions sees
 * an empty array; that's correct).
 */
import { supabase } from '@/lib/supabase';

/* Current account membership + role for the active user. */
export async function fetchMyMembership(accountId) {
  if (!accountId) return null;
  const { data, error } = await supabase
    .from('emp_account_members')
    .select('user_id, role, joined_at')
    .eq('account_id', accountId)
    .limit(1);
  if (error) throw error;
  return data?.[0] || null;
}

/* List all members in the account. Admin sees all; Reviewer only sees self per RLS. */
export async function listMembers(accountId) {
  if (!accountId) return [];
  const { data, error } = await supabase
    .from('emp_account_members')
    .select('user_id, role, joined_at')
    .eq('account_id', accountId)
    .order('joined_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

/* List invitations on the account.
 * RLS: Admin sees all; Reviewer sees only their own accepted row.
 * Phase 0.7.2 — `token` is included so the admin "Copy invitation
 * link" affordance can build the shareable acceptance URL without
 * a second round-trip. RLS V.III restricts visibility of the row
 * (including `token`) to admins of the account, so non-admin
 * callers never see the value. */
export async function listInvitations(accountId, { onlyPending = false } = {}) {
  if (!accountId) return [];
  let q = supabase
    .from('emp_member_invitations')
    .select('id, target_email, target_role, status, invited_by, accepted_by, created_at, expires_at, accepted_at, revoked_at, token')
    .eq('account_id', accountId)
    .order('created_at', { ascending: false });
  if (onlyPending) q = q.eq('status', 'pending');
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

/* List suspensions on the account.
 * RLS: Admin sees all; Reviewer sees only their own row. */
export async function listSuspensions(accountId, { onlyActive = false } = {}) {
  if (!accountId) return [];
  let q = supabase
    .from('emp_member_suspensions')
    .select('id, member_user_id, status, reason, is_emergency, suspended_by, suspended_at, expires_at, lifted_at, lifted_by')
    .eq('account_id', accountId)
    .order('suspended_at', { ascending: false });
  if (onlyActive) q = q.eq('status', 'active');
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

/* Audit log feed for the account.
 * RLS: Admin sees all rows scoped to account_id; Reviewer sees only
 * rows where actor=self or originating_context.target_member_id=self.
 */
export async function listAuditFeed(accountId, { limit = 50 } = {}) {
  if (!accountId) return [];
  const { data, error } = await supabase
    .from('emp_member_audit_log')
    .select('id, action_class, actor_identity, actor_authority_class, originating_context, timestamp_utc')
    .filter('originating_context->>account_id', 'eq', accountId)
    .order('id', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}
